<template>
    <div>
        <button
            @click="openModal"
            type="button"
            class="relative block w-full bg-white rounded-lg overflow-hidden focus:outline-none"
        >
            <span
                class="absolute inset-0 w-full h-full flex items-center justify-center"
                aria-hidden="true"
            >
                <svg
                    class="h-16 w-16 text-nutreance-green"
                    fill="currentColor"
                    viewBox="0 -1 84 86"
                >
                    <circle
                        class="fill-gray-100 hover:fill-gray-200 stroke stroke-gray-300"
                        opacity="0.9"
                        cx="42"
                        cy="42"
                        r="42"
                    ></circle>
                    <path
                        d="M55.5039 40.3359L37.1094 28.0729C35.7803 27.1869 34 28.1396 34 29.737V54.263C34 55.8604 35.7803 56.8131 37.1094 55.9271L55.5038 43.6641C56.6913 42.8725 56.6913 41.1275 55.5039 40.3359Z"
                    ></path>
                </svg>
            </span>
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                class="fixed z-20 inset-0 overflow-y-auto"
                :open="open"
                @close="closeModal"
            >
                <div
                    class="flex items-end justify-center min-h-screen text-center sm:block p-4"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            :class="[
                                mobileDevice ? 'max-w-lg' : 'max-w-5xl',
                                portrait ? 'h-full' : '',
                                'inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:align-middle sm:p-6',
                            ]"
                        >
                            <div class="absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="closeModal"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="mt-6 relative">
                                <video
                                    ref="video"
                                    @loadedmetadata="onLoadedMetadata"
                                    @timeupdate="onTimeUpdated"
                                    @ended="onEnded"
                                    :class="[
                                        portrait ? 'vjs-9-16' : 'vjs-16-9',
                                        'video-js vjs-default-skin vjs-big-play-centered',
                                    ]"
                                    role="region"
                                    aria-label="video player"
                                />
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { computed, inject, nextTick, onMounted, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { XIcon } from "@heroicons/vue/outline";
import { useGoogleDataLayer } from "../../functions/useGoogleDataLayer";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    mobileDevice?: string;
    productSlug?: string;
    videoFileName?: string;
}>();

const emitter = inject<AppEmitter>("mitt")!;
const { pushVideoEventToDataLayer } = useGoogleDataLayer();

const open = ref(false);
const portrait = ref(props.mobileDevice && wasOrientation("portrait"));
const orientation = computed(() => (portrait.value ? "portrait" : "landscape"));
const video = ref<any>(null);
const player = ref<any>(null);
const playback = reactive({
    duration: 0,
    progress: 0,
    played: 0,
    percent0: false,
    percent25: false,
    percent50: false,
    percent75: false,
    percent100: false,
});

onMounted(() => {
    emitter.on("openProductVideoModal", () => openModal());

    if (props.mobileDevice) {
        window.addEventListener("orientationchange", onOrientationChange);
    }
});

function wasOrientation(type: string) {
    return window.innerHeight > window.innerWidth
        ? type === "portrait"
        : type === "landscape";
}

function onOrientationChange() {
    // Reverse logic because the innerHeight and innerWidth of the window are calculated
    // before the orientation is changed
    if (wasOrientation("portrait")) {
        portrait.value = false;
    } else if (wasOrientation("landscape")) {
        portrait.value = true;
    }
}

function openModal() {
    open.value = true;
    nextTick(() => {
        player.value = videojs(
            video.value,
            {
                autoplay: true,
                controls: true,
                preload: "metadata",
                sources: [
                    {
                        src: videoFilePath("mp4"),
                        type: "video/mp4",
                    },
                    {
                        src: videoFilePath("webm"),
                        type: "video/webm",
                    },
                ],
            },
            () => player.value.currentTime(playback.played),
        );
    });
}

function closeModal() {
    if (player.value) {
        setTimeout(() => player.value.dispose(), 500);
    }

    open.value = false;
    emitter.emit("closeProductVideoModal");
}

function videoFilePath(format: string) {
    return (
        import.meta.env.VITE_VIDEO_URL +
        `/products/${props.productSlug}/${orientation.value}/${props.videoFileName}.${format}`
    );
}

function onLoadedMetadata() {
    if (
        !open.value ||
        playback.percent0 ||
        !player.value ||
        !player.value.duration()
    ) {
        return;
    }

    const duration = parseInt(player.value.duration().toFixed(0));

    if (!duration) {
        return;
    }

    playback.duration = duration;
    playback.percent0 = true;

    const attributes = {
        videoVisible: true,
        videoCurrentTime: 0,
        videoDuration: playback.duration,
        videoPercent: 0,
        videoProvider: "videojs",
        videoStatus: "start",
        videoTitle: props.productSlug,
        videoUrl: videoFilePath("mp4"),
    };

    pushVideoEventToDataLayer(attributes);
}

function onTimeUpdated() {
    if (
        !open.value ||
        !player.value ||
        !player.value.currentTime() ||
        !player.value.duration()
    ) {
        return;
    }

    const currentTime = parseInt(player.value.currentTime().toFixed(0));

    if (playback.played > currentTime || !currentTime) {
        return;
    }

    playback.played = currentTime;

    playback.progress = parseInt(
        ((100 * playback.played) / player.value.duration()).toFixed(0),
    );

    switch (true) {
        case !playback.percent25 && playback.progress > 25:
            playback.percent25 = true;
            break;
        case !playback.percent50 && playback.progress > 50:
            playback.percent50 = true;
            break;
        case !playback.percent75 && playback.progress > 75:
            playback.percent75 = true;
            break;
        default:
            return;
    }

    const attributes = {
        videoVisible: true,
        videoCurrentTime: playback.played,
        videoDuration: playback.duration,
        videoPercent: playback.progress,
        videoProvider: "videojs",
        videoStatus: "progress",
        videoTitle: props.productSlug,
        videoUrl: videoFilePath("mp4"),
    };

    pushVideoEventToDataLayer(attributes);
}

function onEnded() {
    if (
        !open.value ||
        playback.percent100 ||
        !player.value ||
        !player.value.currentTime() ||
        !player.value.duration()
    ) {
        return;
    }

    playback.percent100 = true;

    const attributes = {
        videoVisible: true,
        videoCurrentTime: playback.duration,
        videoDuration: playback.duration,
        videoPercent: 100,
        videoProvider: "videojs",
        videoStatus: "complete",
        videoTitle: props.productSlug,
        videoUrl: videoFilePath("mp4"),
    };

    playback.progress = 0;
    playback.played = 0;
    playback.percent0 = false;
    playback.percent25 = false;
    playback.percent50 = false;
    playback.percent75 = false;
    playback.percent100 = false;

    pushVideoEventToDataLayer(attributes);
}
</script>

<style>
/* video.js renders its controls outside this component's scoped DOM, so these overrides are global by design. */
.vjs-poster {
    outline: none;
}

.video-js .vjs-control-bar {
    font-size: 16px;
}
</style>
